import React, { useMemo, useState } from "react";
import { Outlet, useParams } from "react-router-dom";
import {
  Brain,
  Compass,
  MessagesSquare,
  Puzzle,
  Settings,
  User,
  UserRoundPlus,
  Zap,
} from "lucide-react";
import { mutate } from "swr";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import { Switch } from "~/components/ui/switch";
import useToast from "~/components/ui/toast";
import useNextGenHeader from "~/contexts/nextgen-header";
import { usePageContext } from "~/contexts/page-context";
import useUser from "~/contexts/user";
import { useDocumentTitle } from "~/hooks/useDocumentTitle";
import { useFetcher } from "~/hooks/useFetcher";
import { buildEntityBreadcrumbs } from "~/pages/manifold-nextgen/breadcrumbs";
import { ResourceLoadError } from "~/pages/manifold-nextgen/components/ResourceLoadError";
import { SectionNavLink } from "~/pages/manifold-nextgen/components/SectionNavLink";
import { SectionSidebar } from "~/pages/manifold-nextgen/components/SectionSidebar";
import { AgentShareRecipientWarning } from "~/pages/manifold-nextgen/components/agents/AgentShareRecipientWarning";
import { QuickActionsSidebarList } from "~/pages/manifold-nextgen/components/quick-actions/QuickActionsSidebarList";
import { OwnersOnlyDialog } from "~/pages/manifold-nextgen/components/share/OwnersOnlyDialog";
import { ShareModal } from "~/pages/manifold-nextgen/components/share/ShareModal";
import { useAgentShare } from "~/pages/manifold-nextgen/hooks/useAgentShare";
import { useCanDeleteAgent } from "~/pages/manifold-nextgen/hooks/useCanDeleteAgent";
import { useCanDuplicateAgent } from "~/pages/manifold-nextgen/hooks/useCanDuplicateAgent";
import { useCanShareAgent } from "~/pages/manifold-nextgen/hooks/useCanShareAgent";
import { useCanToggleAgentStatus } from "~/pages/manifold-nextgen/hooks/useCanToggleAgentStatus";
import { ROUTE_NEXTGEN_VIEW } from "~/router/constants";
import {
  Agent,
  AgentAccessLevel,
  AgentScope,
  AgentStatus,
  agentPath,
  mutateAgentList,
  updateAgent,
} from "~/services/agents";
import { QuickActionPlacement } from "~/services/quickActions";

// The Cancer Data Scientist is the SYSTEM agent backing the Dataset Assistant
// (app_id DATASET_ASSISTANT). Disabling it would hide the platform's flagship
// assistant from every user, so its status toggle stays locked even for the
// editors/owners who can otherwise flip SYSTEM agents. Matched by stable name +
// scope, not display_name, so CUSTOMER copies remain freely toggleable.
const DATASET_ASSISTANT_AGENT_NAME = "dataset-assistant";

const NAV_ITEMS = [
  { label: "Overview", path: "overview", icon: <User className="size-4" /> },
  {
    label: "Guide",
    path: "guide",
    icon: <Compass className="size-4" />,
  },
  { label: "Behavior", path: "behavior", icon: <Brain className="size-4" /> },
  { label: "Skills", path: "skills", icon: <Zap className="size-4" /> },
  { label: "Capabilities", path: "capabilities", icon: <Puzzle className="size-4" /> },
  { label: "Sessions", path: "sessions", icon: <MessagesSquare className="size-4" /> },
] as const;

export const AgentDetail = () => {
  const { agentId } = useParams<{ agentId: string }>();
  const { data: agent, error, isLoading } = useFetcher<Agent>(agentPath(agentId!));
  const canDelete = useCanDeleteAgent(agent);
  const currentUser = useUser();
  const { showError } = useToast();

  const [enableConfirmOpen, setEnableConfirmOpen] = useState(false);
  const [isToggling, setIsToggling] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  // Status is part of the `can_edit` capability (editors/owners), uniform across
  // scopes: a SYSTEM agent's config is immutable but its status is toggleable, and
  // a plain manage:agents scope without the FGA grant is not enough — matching the
  // backend, which rides the status toggle on the same can_edit path.
  const canToggleStatus = useCanToggleAgentStatus(agent);

  // The flagship SYSTEM dataset-assistant is shown with its status toggle but
  // locked, so it can never be hidden from users.
  const isStatusLocked =
    !!agent && agent.scope === AgentScope.SYSTEM && agent.name === DATASET_ASSISTANT_AGENT_NAME;

  // Whether the caller may share resolves from the manager-only can_share grant
  // (see useCanShareAgent) and applies to both scopes — a SYSTEM agent is
  // shareable by an owner. It gates which modal the Share action opens: the
  // full ShareModal for a sharer, or a read-only owners list for anyone else.
  const canShare = useCanShareAgent(agent);
  const canDuplicate = useCanDuplicateAgent(agent);

  // Fetched unconditionally: sharers need it loaded before the modal opens
  // (a role change is a client-side revoke-then-grant keyed on the current
  // role, so a still-loading list would skip the revoke), and non-sharers need
  // it to see who the agent's owners are.
  const {
    collaborators,
    isLoading: collaboratorsLoading,
    collaboratorsError,
    handleShare,
    accessLevels,
    defaultAccessLevel,
  } = useAgentShare(agentId!);

  useDocumentTitle(agent?.display_name ?? "");

  const headerBreadcrumbs = useMemo(
    () =>
      buildEntityBreadcrumbs({
        rootHref: `${ROUTE_NEXTGEN_VIEW}/agents`,
        entityName: agent?.display_name,
      }),
    [agent?.display_name]
  );

  const headerActions = useMemo<[React.ReactNode?]>(() => {
    // Duplicate / delete moved to the Settings page reached from the sidebar.
    // Share is always shown; canShare below picks which modal it opens.
    const shareNode = (
      <Button key="share-agent" size="sm" className="text-sm" onClick={() => setShareOpen(true)}>
        <UserRoundPlus className="size-4" />
        Share
      </Button>
    );

    return [shareNode];
  }, []);

  useNextGenHeader({ breadcrumbs: headerBreadcrumbs, actions: headerActions });
  usePageContext(agent ? { page: "agent", name: agent.display_name, id: agent.id } : null);

  const doToggle = async (status: AgentStatus) => {
    setIsToggling(true);
    try {
      await updateAgent(agentId!, { status });
      await Promise.all([mutate(agentPath(agentId!)), mutateAgentList()]);
    } catch {
      showError("Failed to update agent status");
    } finally {
      setIsToggling(false);
      setEnableConfirmOpen(false);
    }
  };

  const handleToggleStatus = (checked: boolean) => {
    if (checked) {
      setEnableConfirmOpen(true);
    } else {
      doToggle(AgentStatus.DISABLED);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-full">
        <div className="w-48 shrink-0 border-r border-border px-4 py-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="mx-1 my-1 h-8 animate-pulse rounded-md bg-muted" />
          ))}
        </div>
        <div className="flex-1 p-6">
          <div className="h-64 animate-pulse rounded-lg bg-muted" />
        </div>
      </div>
    );
  }

  if (error || !agent) {
    return <ResourceLoadError error={error} resourceLabel="agent" className="py-16" />;
  }

  return (
    <>
      <div className="flex h-full overflow-hidden">
        <SectionSidebar ariaLabel="Agent sections">
          {canToggleStatus && (
            <div className="flex flex-col gap-1 border-b border-border px-4 py-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-foreground">Enabled</span>
                <Switch
                  checked={agent.status === AgentStatus.ENABLED}
                  onCheckedChange={handleToggleStatus}
                  disabled={isToggling || isStatusLocked}
                  aria-label="Enable agent"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                {agent.status === AgentStatus.ENABLED
                  ? "This agent is visible to users"
                  : "This agent is hidden from users"}
              </p>
            </div>
          )}

          <QuickActionsSidebarList placement={QuickActionPlacement.AGENT} placementId={agent.id} />

          <div className="flex flex-col gap-1 px-4 py-3">
            {NAV_ITEMS.map(({ label, path, icon }) => (
              <SectionNavLink key={path} to={path} end icon={icon} label={label} />
            ))}
            {(canDuplicate || canDelete) && (
              <SectionNavLink
                to="settings"
                end
                icon={<Settings className="size-4" />}
                label="Settings"
              />
            )}
          </div>
        </SectionSidebar>

        <div className="flex-1 overflow-y-auto p-6">
          <Outlet context={agent} />
        </div>
      </div>

      <AlertDialog open={enableConfirmOpen} onOpenChange={setEnableConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Enable &ldquo;{agent.display_name}&rdquo;?</AlertDialogTitle>
            <AlertDialogDescription>
              Once enabled, this agent becomes visible to users, can be added to projects, and used
              in conversations.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isToggling}>Cancel</AlertDialogCancel>
            <Button onClick={() => doToggle(AgentStatus.ENABLED)} disabled={isToggling}>
              {isToggling ? "Enabling…" : "Enable"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {canShare ? (
        <ShareModal
          open={shareOpen}
          onOpenChange={setShareOpen}
          resourceLabel="agent"
          resourceName={agent.display_name}
          collaborators={collaborators}
          collaboratorsLoading={collaboratorsLoading}
          collaboratorsError={collaboratorsError}
          accessLevels={accessLevels}
          defaultAccessLevel={defaultAccessLevel}
          onShare={handleShare}
          currentUserEmail={currentUser?.email}
          allowGeneralAccess
          renderRecipientWarning={(recipient) => (
            <AgentShareRecipientWarning agentId={agent.id} recipient={recipient} />
          )}
        />
      ) : (
        <OwnersOnlyDialog
          open={shareOpen}
          onOpenChange={setShareOpen}
          resourceLabel="agent"
          resourceName={agent.display_name}
          ownerLevel={AgentAccessLevel.OWNER}
          collaborators={collaborators}
          collaboratorsLoading={collaboratorsLoading}
          collaboratorsError={collaboratorsError}
        />
      )}
    </>
  );
};
